import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Resumen from './pages/reportes/Resumen';
import Ventas from './pages/reportes/Ventas';
import ClientesReporte from './pages/reportes/Clientes';
import Productos from './pages/menu/Productos';
import Comandas from './pages/comandas/Comandas';
import Cuentas from './pages/cuentas/Cuentas';
import Corte from './pages/Corte';
import Categorias from './pages/Categorias';
import Equipo from './pages/ajustes/Equipo';
import Ajustes from './pages/ajustes/Ajustes';
import Tiempos from './pages/reportes/Tiempos';
import Alertas from './pages/reportes/Alertas';
import Inventario from './pages/almacen/Inventario';
import Movimientos from './pages/almacen/Movimientos';
import Mermas from './pages/Mermas';
import ComidaEmpleados from './pages/ComidaEmpleados';
import Auditoria from './pages/Auditoria';
import Agenda from './pages/agenda/Agenda';
import Turno from './pages/turno/Turno';
import Requisiciones from './pages/Requisiciones';
import Promociones from './pages/menu/Promociones';
import Salon from './pages/salon/Salon';
import Modificadores from './pages/menu/Modificadores';
import ObjetivosSmart from './pages/ObjetivosSmart';
import Tickets from './pages/tickets/Tickets';
import Proveedores from './pages/almacen/Proveedores';

function PrivateRoute({ children, roles, permiso, estacion }: { children: React.ReactElement; roles?: string[]; permiso?: string; estacion?: 'cocina' | 'barra' }) {
  const { token, usuario } = useAuth();
  if (!token) return <Navigate to="/" />;
  // Acceso si el rol está en la lista, O si el usuario tiene el permiso granular
  // asignado (ej. un mesero de confianza con acceso a Reportes sin ser admin).
  if (roles && usuario) {
    const porRol = roles.includes(usuario.rol);
    const porPermiso = permiso ? !!usuario[permiso] : false;
    if (!porRol && !porPermiso) return <Navigate to="/dashboard" />;
  }
  // KDS por estación: un usuario 'cocina' asignado solo a Barra no puede entrar
  // a Cocina y viceversa. 'ambas' (o admin) siempre tiene acceso a los dos.
  if (estacion && usuario?.rol === 'cocina' && usuario.estacion && usuario.estacion !== 'ambas' && usuario.estacion !== estacion) {
    return <Navigate to="/dashboard" />;
  }
  return children;
}

// Después del login todos llegan a /dashboard. Admin y cajero ven el Resumen;
// los demás roles, directo a su pantalla de trabajo.
const INICIO_POR_ROL: Record<string, string> = { mesero: '/comandera', cocina: '/comandas', almacenista: '/inventario' };
function Inicio() {
  const { usuario } = useAuth();
  const destino = INICIO_POR_ROL[usuario?.rol ?? ''];
  if (destino) return <Navigate to={destino} replace />;
  if (usuario?.rol !== 'admin' && usuario?.rol !== 'cajero') return <Navigate to="/" replace />;
  return <Layout><Resumen /></Layout>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/dashboard" element={
        <PrivateRoute><Inicio /></PrivateRoute>
      } />
      <Route path="/productos" element={
        <PrivateRoute roles={['admin']}><Layout><Productos /></Layout></PrivateRoute>
      } />
      {/* La página Mesas era un subconjunto de la Comandera (mismo mapa,
          misma gestión de estado/personas/unión): consolidada ahí. */}
      <Route path="/mesas" element={<Navigate to="/comandera" replace />} />
      {/* Cocina y barra viven en un solo tablero con selector de estación; la
          estación asignada al usuario la limita el propio tablero. */}
      <Route path="/comandas" element={
        <PrivateRoute roles={['admin','cocina']}><Layout><Comandas /></Layout></PrivateRoute>
      } />
      <Route path="/cocina" element={<Navigate to="/comandas?e=cocina" replace />} />
      <Route path="/barra" element={<Navigate to="/comandas?e=barra" replace />} />
      <Route path="/turno" element={
        <PrivateRoute roles={['admin','cajero']}><Layout><Turno /></Layout></PrivateRoute>
      } />
      <Route path="/requisiciones" element={
        <PrivateRoute roles={['admin','cocina','almacenista']}><Layout><Requisiciones /></Layout></PrivateRoute>
      } />
      <Route path="/caja" element={
        <PrivateRoute roles={['admin','cajero']}><Layout><Cuentas /></Layout></PrivateRoute>
      } />
      <Route path="/corte" element={
        <PrivateRoute roles={['admin','cajero']}><Layout><Corte /></Layout></PrivateRoute>
      } />
      {/* Reporte fusionado (antes /reportes + /reporte-ventas): el acceso es
          la UNIÓN de los dos viejos para que nadie pierda la página. */}
      <Route path="/reportes" element={
        <PrivateRoute roles={['admin','cajero']} permiso="puede_ver_reportes"><Layout><Ventas /></Layout></PrivateRoute>
      } />
      <Route path="/reportes/clientes" element={
        <PrivateRoute roles={['admin','cajero']} permiso="puede_ver_reportes"><Layout><ClientesReporte /></Layout></PrivateRoute>
      } />
      <Route path="/categorias" element={
        <PrivateRoute roles={['admin']}><Layout><Categorias /></Layout></PrivateRoute>
      } />
      {/* Reservaciones y eventos viven juntos en Agenda (pestañas). */}
      <Route path="/agenda" element={
        <PrivateRoute roles={['admin','mesero']}><Layout><Agenda /></Layout></PrivateRoute>
      } />
      <Route path="/reservaciones" element={<Navigate to="/agenda" replace />} />
      <Route path="/eventos" element={<Navigate to="/agenda?t=eventos" replace />} />
      <Route path="/auditoria" element={
        <PrivateRoute roles={['admin']} permiso="puede_ver_auditoria"><Layout><Auditoria /></Layout></PrivateRoute>
      } />
      <Route path="/inventario" element={
        <PrivateRoute roles={['admin','cocina','almacenista']}><Layout><Inventario /></Layout></PrivateRoute>
      } />
      <Route path="/movimientos" element={
        <PrivateRoute roles={['admin','cocina','almacenista']}><Layout><Movimientos /></Layout></PrivateRoute>
      } />
      <Route path="/mermas" element={
        <PrivateRoute roles={['admin','cocina','almacenista']}><Layout><Mermas /></Layout></PrivateRoute>
      } />
      <Route path="/comida-empleados" element={
        <PrivateRoute roles={['admin','cocina','almacenista']}><Layout><ComidaEmpleados /></Layout></PrivateRoute>
      } />
      <Route path="/alertas" element={
        <PrivateRoute roles={['admin']}><Layout><Alertas /></Layout></PrivateRoute>
      } />
      <Route path="/tiempos" element={
        <PrivateRoute roles={['admin']}><Layout><Tiempos /></Layout></PrivateRoute>
      } />
      <Route path="/ventas-por-dia" element={<Navigate to="/reportes/clientes?t=dias" replace />} />
      {/* Ruta vieja del reporte detallado: vive fusionado en /reportes */}
      <Route path="/reporte-ventas" element={<Navigate to="/reportes" replace />} />
      <Route path="/config-ticket" element={
        <PrivateRoute roles={['admin']}><Layout><Ajustes grupo="config" /></Layout></PrivateRoute>
      } />
      <Route path="/config-tickets" element={
        <PrivateRoute roles={['admin']}><Layout><Ajustes grupo="tickets" /></Layout></PrivateRoute>
      } />
      <Route path="/ajustes/*" element={<Navigate to="/config-ticket" replace />} />
      <Route path="/usuarios" element={
        <PrivateRoute roles={['admin']}><Layout><Equipo /></Layout></PrivateRoute>
      } />
      <Route path="/promociones" element={
        <PrivateRoute roles={['admin']}><Layout><Promociones /></Layout></PrivateRoute>
      } />
      <Route path="/comandera" element={
        <PrivateRoute roles={['admin','mesero']}><Layout><Salon /></Layout></PrivateRoute>
      } />
      <Route path="/modificadores" element={
        <PrivateRoute roles={['admin']}><Layout><Modificadores /></Layout></PrivateRoute>
      } />
      <Route path="/objetivos" element={
        <PrivateRoute roles={['admin']}><Layout><ObjetivosSmart /></Layout></PrivateRoute>
      } />
      <Route path="/historial-tickets" element={
        <PrivateRoute roles={['admin','cajero']}><Layout><Tickets /></Layout></PrivateRoute>
      } />
      <Route path="/proveedores" element={
        <PrivateRoute roles={['admin','almacenista']}><Layout><Proveedores /></Layout></PrivateRoute>
      } />
      {/* Los cupones viven en Promociones (un tipo más). */}
      <Route path="/cupones" element={<Navigate to="/promociones?t=cupon" replace />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
} 